import { useEffect, useState } from "react";
import "react-toastify/dist/ReactToastify.min.css";
import { ToastContainer } from "react-toastify";

import GoldenRod from "./GoldenRod";
import Header from "./header/Header";
import LoadingSvg2 from "./LoadingSvg2";
import RbA from "./RbA";
import StudentsContainer from "./StudentsContainer";
import Toolbar from "./Toolbar";
import WelcomeText from "./WelcomeText";
import { getActionControl, isAllowed, isWindowOpen } from "@utils/ActionControlWindows";
import { GetLang, translate } from "./translations/Translations";
import { getCountdownDays, getEnglishDate, getEpoch, getNow } from "@utils/DateFormatter";
import { useGlobalContext } from "./contextProvider/ContextProvider";
import { useDashboardData } from "@hooks/useDashboardData";

import "@/styles/global/Global.scss";
import "@/styles/WelcomeText.scss";

/**
 * Guardian Dashboard Main
 * @name Main
 * @constructor
 * @return {JSX.Element}
 */
const Main = (): JSX.Element => {
    const { state } = useGlobalContext();
    const {
        guardianState,
        oeActionControls,
        oeActiveDistrictWindow,
        oeSchoolYearRange,
        oeDistrictWindows,
        studentsIc,
        studentsOe
    } = state || {};

    const lang = GetLang();

    const { loading, error } = useDashboardData(); // Centralized data fetching

    const [addAllowed, setAddAllowed] = useState(false);
    const [broadcast, setBroadcast] = useState(false);
    const [schoolYearRange, setSchoolYearRange] = useState("2027 - 2028");
    const [prStartDate, setPrStartDate] = useState("2026-11-01 08:00:00");

    const allowedRolesArray = ["EXTERNAL_GUARDIAN", "GUARDIAN"];

    // Broadcast message components if Broadcast is true
    const goldenRodBody = translate("Broadcast_Message", lang);
    const goldenRodHeadline = translate("Broadcast_Title", lang);

    useEffect(() => {
        const rightNow = getNow();
        const start = getEpoch("2025-10-31 00:00:00");
        const end = getEpoch("2025-11-21 11:59:59");
        if (start && end && rightNow >= start && rightNow < end) {
            setBroadcast(true);
        }
    }, []);

    /**
     * Determine if the user is allowed to add a student
     */
    useEffect(() => {
        if (!oeActionControls || !oeActiveDistrictWindow || !oeDistrictWindows) {
            setAddAllowed(false);
            return;
        }
        if (
            isAllowed("PARENT_REQUEST", oeActionControls, oeActiveDistrictWindow, oeDistrictWindows) &&
            isWindowOpen(oeActiveDistrictWindow.startDate, oeActiveDistrictWindow.endDate)
        ) {
            setAddAllowed(true);
        } else {
            setAddAllowed(false);
        }
    }, [oeActionControls, oeActiveDistrictWindow, oeDistrictWindows]);

    useEffect(() => {
        if (oeSchoolYearRange) {
            const [start, end] = oeSchoolYearRange.name.split(" - ");
            setSchoolYearRange(`${Number(start) + 1} - ${Number(end) + 1}`);
        }
    }, [oeSchoolYearRange]);

    useEffect(() => {
        if (oeActionControls && oeActiveDistrictWindow) {
            const prActionControl = getActionControl("PARENT_REQUEST", oeActionControls, oeActiveDistrictWindow);
            if (prActionControl) {
                setPrStartDate(prActionControl.startDate);
            }
        }
    }, [oeActionControls, oeActiveDistrictWindow]);

    // Display error message if the hook fails
    if (error) {
        return <div>Error loading dashboard data. Please try again later.</div>;
    }

    const hasStudents = Boolean((studentsOe && studentsOe.length > 0) || (studentsIc && studentsIc.length > 0));

    if (!loading && !guardianState && !addAllowed && !hasStudents) {
        const countdownDays = getCountdownDays(prStartDate);
        const formattedStartDate = getEnglishDate(prStartDate);

        return (
            <RbA allowedRoles={allowedRolesArray} redirect="/notFound">
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <ToastContainer />
                    <Header />
                    {/* DCSD Guardian Dashboard */}
                    <Toolbar label={translate("Breadcrumb_Back", lang)} />
                    <div className="gutter-96">
                        <div className="oe-pending-container">
                            <div className="oe-pending-hero">
                                <h1 className="oe-header-title">
                                    {translate("Welcome_Header_Alt", lang) || "Welcome to the EngagED Parent Portal"}
                                </h1>
                                <p className="oe-pending-desc">
                                    {translate("OE_Pending_Desc_Pre", lang)}
                                    <strong>{schoolYearRange}</strong>
                                    {translate("OE_Pending_Desc_Post", lang)}
                                </p>
                                <div className="oe-countdown-banner">
                                    <div className="oe-countdown-text">
                                        {translate("OE_Pending_Begins", lang)}
                                        <span className="oe-highlight-date">{formattedStartDate}</span>.
                                    </div>
                                    {countdownDays !== null && (
                                        <div className="oe-countdown-badge">
                                            <span>⏳</span> {translate("OE_Countdown_Opens_In", lang)}
                                            {countdownDays}{" "}
                                            {Number(countdownDays) === 1
                                                ? translate("OE_Countdown_Day", lang)
                                                : translate("OE_Countdown_Days", lang)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="oe-resource-grid">
                                <div className="oe-resource-card">
                                    <div className="oe-card-content">
                                        <h3>{translate("OE_Explore_Schools_Title", lang)}</h3>
                                        <p>{translate("OE_Explore_Schools_Desc", lang)}</p>
                                    </div>
                                    <a
                                        className="oe-card-link"
                                        href="https://www.dcsdk12.org/schools-academics/schools"
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        {translate("OE_Explore_Schools_Btn", lang)}
                                    </a>
                                </div>

                                <div className="oe-resource-card">
                                    <div className="oe-card-content">
                                        <h3>{translate("OE_Info_Title", lang)}</h3>
                                        <p>{translate("OE_Info_Desc", lang)}</p>
                                    </div>
                                    <a
                                        className="oe-card-link"
                                        href="https://www.dcsdk12.org/about/departments/choice-programming/open-enrollment"
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        {translate("OE_Info_Btn", lang)}
                                    </a>
                                </div>
                            </div>

                            <div className="oe-help-footer">
                                <strong>{translate("OE_Help_Footer_Title", lang)}</strong>
                                {translate("OE_Help_Footer_Desc", lang)}
                            </div>
                        </div>
                    </div>
                </div>
            </RbA>
        );
    }

    return (
        <RbA allowedRoles={allowedRolesArray} redirect="/notFound">
            <div style={{ display: "flex", flexDirection: "column" }}>
                <ToastContainer />
                <Header />
                {/* DCSD Guardian Dashboard */}
                <Toolbar label={translate("Breadcrumb_Back", lang)} />
                <div className="gutter-96">
                    {!guardianState && <WelcomeText />}
                    {broadcast && <GoldenRod headline={goldenRodHeadline} bodyText={goldenRodBody} />}
                    <StudentsContainer loader={loading} />
                </div>
                {loading && <LoadingSvg2 />}
            </div>
        </RbA>
    );
};

export default Main;
